import React from 'react';
import { ProductType } from 'Quotes/types/productTypes';
import FttpAggregationCharge from './FttpAggregationCharge';
import IPCharge from './IPCharge';
import PriceSummarySegment from './PriceSummarySegment';
import { featureFlag } from 'FeatureFlags/utils/hasFeatureEnabled';
import { Feature } from 'FeatureFlags/types';
import { IPType } from '../../../Configure/DIAConfig/types/ip';
import { IAEndLocation } from 'Quotes/types/store';
import { AnnualExtras } from 'Quotes/QuoteBuilder/utils/costsUtilities';
import { IMDIAPrices } from 'Quotes/QuoteBuilder/components/Price/components/TillReceipt/UpfrontCosts';
import { currencyFormatter } from 'shared/utils/currencyFormatter';
import { Charges } from './TillReceipt';
import { TIER_NAMES } from '../DDOSProtection';
import { cloudProviderForDisplay } from 'Order/OrderBuilder/shared/utils/cloudProviderForDisplay';
import { selectSelectedPrice } from 'Quotes/selectors';
import { useSelector } from 'react-redux';
import { displayAnnualAmount, useMonthlyDisplayPreference } from 'Quotes/utils/priceDisplay';

interface AnnualChargesExtraProps {
  includeFTTPAggregationCharge: boolean;
  productType: ProductType | null | undefined;
  ipType?: IPType;
  isManagedDIA: boolean;
  diaIPAllocation: IAEndLocation['dia_ip_allocation'];
  priceAmortised: boolean;
  charges: Charges;
  annualCharges: AnnualExtras;
  mdiaPrices: IMDIAPrices | undefined;
  rapid_activation?: boolean;
  ddos_protection?: string;
  note?: string;
}

const AnnualChargesExtra: React.FC<AnnualChargesExtraProps> = ({
  includeFTTPAggregationCharge,
  productType,
  ipType,
  isManagedDIA,
  diaIPAllocation,
  annualCharges,
  charges,
  rapid_activation,
  ddos_protection,
  note
}) => {
  const selectedPrice = useSelector(selectSelectedPrice);
  const viewAsMonthly = useMonthlyDisplayPreference();
  return (
    <>
      {includeFTTPAggregationCharge && (
        <FttpAggregationCharge fttpAggregationCharge={annualCharges.fttpAggregationCharge} viewAsMonthly={viewAsMonthly} />
      )}

      {rapid_activation && (
        <>
          {(annualCharges.rapidActivationPrice ?? 0) > 0 && 
            <div className="price-summary__segment-wrapper font-italic pl-2">
              <div>Includes: {viewAsMonthly ? 'Monthly' : 'Annual'} charge for Rapid activation</div>
              <div className="d-flex flex-column align-items-end flex-fill">
                <b className="total currency">{currencyFormatter.format(displayAnnualAmount(annualCharges.rapidActivationPrice ?? 0, viewAsMonthly) ?? 0)}</b>
              </div>
            </div>
          }
          {(charges.rapidActivationPrice ?? 0) > 0 && 
            <div className="price-summary__segment-wrapper font-italic pl-2">
              <div>Includes: One-off charge for Rapid activation </div>
              <div className="d-flex flex-column align-items-end flex-fill">
                <b className="total currency">{currencyFormatter.format(charges.rapidActivationPrice ?? 0)}</b>
              </div>
            </div>
          }
        </>
      )}

      {productType === ProductType.DIA && (
        <>
          <IPCharge
            ipType={ipType}
            isManagedDIA={isManagedDIA}
            diaIPAllocation={diaIPAllocation}
            ipCost={annualCharges.ipCost}
            viewAsMonthly={viewAsMonthly}
          />
          {featureFlag.isEnabled(Feature.mdiaPriceBreakdown) && isManagedDIA && annualCharges.mdiaAnnualPrice > 0 && (
            <PriceSummarySegment
              label="Includes managed DIA services at"
              value={annualCharges.mdiaAnnualPrice}
              suffix={viewAsMonthly ? 'per month' : 'per year'}
              viewAsMonthly={viewAsMonthly}
            />
          )}

          {/* MDIA prices should remain an up-front (one-off) line and must not be amortised into annual charges. */}

          {ddos_protection && (
            <PriceSummarySegment
              label={`Includes DDoS protection (${TIER_NAMES[ddos_protection]}) at`}
              value={(annualCharges.ddosProtectionPrice ?? 0) + (annualCharges.ddosProtectionCost ?? 0)}
              suffix={viewAsMonthly ? 'per month' : 'per year'}
              viewAsMonthly={viewAsMonthly}
            />
          )}

          {selectedPrice.cloud_connect?.provider && selectedPrice.cloud_connect?.bandwidth && (
            <PriceSummarySegment
              label={`Includes Cloud Connect (${cloudProviderForDisplay(selectedPrice.cloud_connect?.provider)} at ${selectedPrice.cloud_connect.bandwidth >= 1000 ? `${selectedPrice.cloud_connect.bandwidth / 1000} Gbps` : `${selectedPrice.cloud_connect.bandwidth} Mbps`}) at`}
              value={selectedPrice.cloud_connect?.annual_price ?? 0}
              suffix={viewAsMonthly ? 'per month' : 'per year'}
              viewAsMonthly={viewAsMonthly}
            />
          )}          
        </>
      )}
      
      {annualCharges.annualDiscount !== 0 && (
        <PriceSummarySegment
          label={`Includes ${viewAsMonthly ? 'monthly' : 'annual'} discount of`}
          value={annualCharges.annualDiscount}
          viewAsMonthly={viewAsMonthly}
        />
      )}

      {note && (
        <div className="price-summary__segment-wrapper pl-2">
          <div><small>{note}</small></div>
        </div>
      )}
    </>
  );
};

export default AnnualChargesExtra;
